import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Menu, X, Sparkles } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { INSTAGRAM_URL } from "../data/products";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NavLinks = ({ onClick, className }) => {
  const { t } = useLanguage();
  const links = [
    { to: "/", label: t.nav.home },
    { to: "/behandelingen", label: t.nav.services },
    { to: "/collectie", label: t.nav.collection },
    { to: "/hoe-het-werkt", label: t.nav.how },
    { to: "/contact", label: t.nav.contact },
  ];
  return (
    <nav className={className} aria-label="Main">
      {links.map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          onClick={onClick}
          data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
          className={({ isActive }) =>
            `link-underline text-sm font-semibold tracking-wide transition-colors duration-300 ${
              isActive ? "text-rouge" : "text-ink hover:text-rouge"
            }`
          }
        >
          {l.label}
        </NavLink>
      ))}
    </nav>
  );
};

const LangToggle = () => {
  const { lang, setLang } = useLanguage();
  return (
    <div data-testid="lang-toggle" className="flex items-center border border-ink/20 text-xs font-bold tracking-widest">
      {["nl", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`px-2.5 py-1.5 uppercase transition-colors duration-300 ${
            lang === l ? "bg-ink text-cream" : "text-ink hover:text-rouge"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Newsletter = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      setDone(true);
      toast.success(t.footer.newsletterDone);
    } catch {
      toast.error(t.footer.newsletterDone);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return <p data-testid="newsletter-success" className="text-blush text-sm font-semibold">{t.footer.newsletterDone}</p>;
  }
  return (
    <form onSubmit={submit} className="flex w-full max-w-sm" data-testid="newsletter-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t.footer.newsletterPlaceholder}
        aria-label={t.footer.newsletterTitle}
        data-testid="newsletter-email-input"
        className="w-full border border-cream/25 bg-transparent px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-blush focus:outline-none transition-colors duration-300"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="newsletter-submit-button"
        className="shrink-0 border border-rouge bg-rouge px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-rouge-dark disabled:opacity-60"
      >
        {t.footer.newsletterCta}
      </button>
    </form>
  );
};

const Layout = ({ children }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-cream">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-ink/5 bg-cream/80 backdrop-blur-xl" data-testid="site-header">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-12">
          <Link to="/" data-testid="logo-link" className="font-display text-2xl font-bold tracking-tight">
            On It <span className="italic font-medium text-rouge">Nails</span>
          </Link>
          <NavLinks className="hidden items-center gap-8 lg:flex" />
          <div className="hidden items-center gap-4 lg:flex">
            <LangToggle />
            <Link
              to="/behandelingen#boeken"
              data-testid="nav-book-cta"
              className="group relative overflow-hidden border border-ink bg-ink px-6 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-rouge hover:border-rouge"
            >
              {t.nav.book}
            </Link>
          </div>
          <button
            className="lg:hidden text-ink"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-button"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden border-t border-ink/10 bg-cream lg:hidden"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col gap-5 px-6 py-8">
                <NavLinks onClick={() => setOpen(false)} className="flex flex-col gap-5" />
                <div className="flex items-center gap-4 pt-2">
                  <LangToggle />
                  <Link
                    to="/behandelingen#boeken"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-book-cta"
                    className="bg-rouge px-6 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-cream"
                  >
                    {t.nav.book}
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main key={location.pathname}>{children}</main>

      <footer className="bg-ink text-cream" data-testid="site-footer">
        <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-12 lg:py-28">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="font-display text-4xl font-bold leading-tight lg:text-5xl">
                On It <span className="italic font-medium text-blush">Nails</span>
              </p>
              <p className="mt-5 max-w-sm text-sm leading-relaxed text-cream/60">{t.footer.tagline}</p>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram-link"
                className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blush transition-colors duration-300 hover:text-cream"
              >
                <Instagram size={16} /> Instagram
              </a>
            </div>
            <div className="lg:col-span-3">
              <p className="overline-label text-cream/40">{t.footer.explore}</p>
              <div className="mt-6 flex flex-col gap-3 text-sm">
                <Link className="link-underline w-fit text-cream/80 hover:text-cream transition-colors duration-300" to="/behandelingen">{t.nav.services}</Link>
                <Link className="link-underline w-fit text-cream/80 hover:text-cream transition-colors duration-300" to="/collectie">{t.nav.collection}</Link>
                <Link className="link-underline w-fit text-cream/80 hover:text-cream transition-colors duration-300" to="/hoe-het-werkt">{t.nav.how}</Link>
                <Link className="link-underline w-fit text-cream/80 hover:text-cream transition-colors duration-300" to="/contact">{t.nav.contact}</Link>
              </div>
              <p className="overline-label mt-10 text-cream/40">{t.footer.contact}</p>
              <div className="mt-6 flex flex-col gap-3 text-sm text-cream/80">
                <span className="inline-flex items-center gap-2"><Sparkles size={14} className="text-blush" /> Herzele, Oost-Vlaanderen</span>
                <span>{t.footer.hours} · {t.footer.region}</span>
              </div>
            </div>
            <div className="lg:col-span-4">
              <p className="overline-label text-cream/40">{t.footer.newsletterTitle}</p>
              <p className="mt-6 text-sm leading-relaxed text-cream/60">{t.footer.newsletterText}</p>
              <div className="mt-6">
                <Newsletter />
              </div>
            </div>
          </div>
          <div className="mt-20 flex flex-col gap-3 border-t border-cream/10 pt-8 text-xs text-cream/40 sm:flex-row sm:items-center sm:justify-between">
            <p>© {new Date().getFullYear()} On It Nails — {t.footer.rights}</p>
            <p className="tracking-wide">press on nagels Herzele · BIAB Zottegem · gellak Herzele</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
